import React from 'react'

import { Link, useLocation, useNavigate } from 'react-router-dom';
export default function FinanceFormPage({ user, basketCount, onToggleMenu, onShowChat, onSwitchRole, onShowLogin }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
    const [formData, setFormData] = React.useState({
        yearsInBusiness: '',
        annualRevenue: '',
        locatedInSA: '',
        listingNumber: '',
        loanAmount: '',
        companyName: '',
        companyRegNumber: '',
        fullName: '',
        contactNumber: '',
        alternateContactNumber: '',
        email: '',
        termsAccepted: false
    });
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [showSuccess, setShowSuccess] = React.useState(false);
    const [showError, setShowError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');

    // Check if we're on mobile
    const isMobile = window.innerWidth < 768;

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            const apiData = {
                company_name: formData.companyName,
                company_reg_number: formData.companyRegNumber,
                contact_person: formData.fullName,
                contact_number: formData.contactNumber,
                alternate_contact_number: formData.alternateContactNumber,
                email: formData.email,
                years_in_business: formData.yearsInBusiness,
                annual_revenue: formData.annualRevenue,
                located_in_sa: formData.locatedInSA,
                listing_number: formData.listingNumber,
                estimated_finance_amount: formData.loanAmount,
                terms_accepted: formData.termsAccepted ? 1 : 0
            };
            
            const response = await fetch('/api/submit_finance_referral.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(apiData)
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowSuccess(true);
                setFormData({
                    yearsInBusiness: '',
                    annualRevenue: '',
                    locatedInSA: '',
                    listingNumber: '',
                    loanAmount: '',
                    companyName: '',
                    companyRegNumber: '',
                    fullName: '',
                    contactNumber: '',
                    alternateContactNumber: '',
                    email: '',
                    termsAccepted: false
                });
            } else {
                setErrorMessage(result.message || 'An error occurred while submitting your request.');
                setShowError(true);
            }
        } catch (error) {
            console.error('Error:', error);
            setErrorMessage('An error occurred while submitting your request. Please try again or contact support.');
            setShowError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        if (onToggleMenu) onToggleMenu();
    };

    return (
        <div data-name="finance-form-page" data-file="pages/FinanceFormPage.js" className="min-h-screen bg-gray-900 pb-24 font-sans">
            {/* Header */}
            <div className="bg-white/10 backdrop-blur-md sticky top-0 z-50 shadow-md px-4 py-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="text-white/80 hover:text-white p-2">
                        <i className="fas fa-arrow-left text-xl"></i>
                    </button>
                    <div className="flex flex-col flex-1">
                        <span className="font-extrabold text-white text-xl">Finance Application</span>
                        <span className="text-white/60 text-sm">Get financing for your equipment</span>
                    </div>
                    {/* Hamburger Menu - Always visible */}
                    <button 
                        onClick={handleToggleMenu}
                        className="ml-auto text-white/80 hover:text-white p-2 relative"
                    >
                        <i className="fas fa-bars text-xl"></i>
                        {basketCount && basketCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                                {basketCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="px-4 py-6">
                <div className="max-w-2xl mx-auto">
                    {/* Logo Section */}
                    <div className="text-center mb-8">
                        <div className="flex justify-center mb-4">
                            <img src="images/logo/MM Logo.jpg" alt="Miners Market Logo" className="h-20 w-auto rounded-lg" />
                        </div>
                        <h1 className="text-3xl font-bold text-white mb-2">Finance Referral Request</h1>
                        <p className="text-gray-300">Miners Market</p>
                    </div>

                    {/* Disclaimer */}
                    <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 mb-6">
                        <p className="text-blue-200 text-sm">
                            <strong>Important Notice:</strong><br />
                            Miners Market does not provide financing, credit assessment, financial advice or lending services.
                            Information submitted through this form is collected solely for referral purposes and may be shared with our referral finance providers.
                        </p>
                    </div>

                    {/* Success Message */}
                    {showSuccess && (
                        <div className="bg-green-600 text-white rounded-lg p-4 mb-6">
                            <div className="flex items-center gap-3">
                                <i className="fas fa-check-circle text-2xl"></i>
                                <div>
                                    <h3 className="font-bold">Success!</h3>
                                    <p className="text-sm">Finance referral request submitted successfully!</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {showError && (
                        <div className="bg-red-600 text-white rounded-lg p-4 mb-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <i className="fas fa-exclamation-circle text-2xl"></i>
                                    <div>
                                        <h3 className="font-bold">Error</h3>
                                        <p className="text-sm">{errorMessage}</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setShowError(false)}
                                    className="text-white/80 hover:text-white"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Finance Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Qualification Questions */}
                        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                            <h3 className="text-white font-bold mb-4">Qualification Questions</h3>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="text-white font-medium block mb-2">
                                        Minimum 1+ years in business? <span className="text-red-400">*</span>
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="text-white/80 flex items-center gap-2">
                                            <input 
                                                type="radio" 
                                                name="yearsInBusiness" 
                                                value="yes" 
                                                checked={formData.yearsInBusiness === 'yes'}
                                                onChange={handleInputChange}
                                                className="text-blue-600"
                                                required
                                            /> Yes
                                        </label>
                                        <label className="text-white/80 flex items-center gap-2">
                                            <input 
                                                type="radio" 
                                                name="yearsInBusiness" 
                                                value="no" 
                                                checked={formData.yearsInBusiness === 'no'}
                                                onChange={handleInputChange}
                                                className="text-blue-600"
                                                required
                                            /> No
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-white font-medium block mb-2">
                                        Is your Annual Revenue R1,000,000 +? <span className="text-red-400">*</span>
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="text-white/80 flex items-center gap-2">
                                            <input 
                                                type="radio" 
                                                name="annualRevenue" 
                                                value="yes" 
                                                checked={formData.annualRevenue === 'yes'}
                                                onChange={handleInputChange}
                                                className="text-blue-600"
                                                required
                                            /> Yes
                                        </label>
                                        <label className="text-white/80 flex items-center gap-2">
                                            <input 
                                                type="radio" 
                                                name="annualRevenue" 
                                                value="no" 
                                                checked={formData.annualRevenue === 'no'}
                                                onChange={handleInputChange}
                                                className="text-blue-600"
                                                required
                                            /> No
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-white font-medium block mb-2">
                                        Are you Located in South Africa? <span className="text-red-400">*</span>
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="text-white/80 flex items-center gap-2">
                                            <input 
                                                type="radio" 
                                                name="locatedInSA" 
                                                value="yes" 
                                                checked={formData.locatedInSA === 'yes'}
                                                onChange={handleInputChange}
                                                className="text-blue-600"
                                                required
                                            /> Yes
                                        </label>
                                        <label className="text-white/80 flex items-center gap-2">
                                            <input 
                                                type="radio" 
                                                name="locatedInSA" 
                                                value="no" 
                                                checked={formData.locatedInSA === 'no'}
                                                onChange={handleInputChange}
                                                className="text-blue-600"
                                                required
                                            /> No
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="listingNumber" className="text-white font-medium block mb-2">
                                        Listing Number
                                    </label>
                                    <input 
                                        type="text" 
                                        id="listingNumber"
                                        name="listingNumber" 
                                        value={formData.listingNumber}
                                        onChange={handleInputChange}
                                        placeholder="Enter listing number (optional)"
                                        className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 focus:bg-white/20 focus:border-blue-400 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="loanAmount" className="text-white font-medium block mb-2">
                                        Estimated finance amount required <span className="text-red-400">*</span>
                                    </label>
                                    <input 
                                        type="number" 
                                        id="loanAmount"
                                        name="loanAmount" 
                                        value={formData.loanAmount}
                                        onChange={handleInputChange}
                                        min="0" 
                                        step="0.01" 
                                        placeholder="R 0.00" 
                                        className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 focus:bg-white/20 focus:border-blue-400 focus:outline-none"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Company Information */}
                        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                            <h3 className="text-white font-bold mb-4">Company Information</h3>
                            
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="companyName" className="text-white font-medium block mb-2">
                                        Company trading name: <span className="text-red-400">*</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        id="companyName"
                                        name="companyName" 
                                        value={formData.companyName}
                                        onChange={handleInputChange}
                                        className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 focus:bg-white/20 focus:border-blue-400 focus:outline-none"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="companyRegNumber" className="text-white font-medium block mb-2">
                                        Company Registration Number <span className="text-red-400">*</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        id="companyRegNumber"
                                        name="companyRegNumber" 
                                        value={formData.companyRegNumber}
                                        onChange={handleInputChange}
                                        className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 focus:bg-white/20 focus:border-blue-400 focus:outline-none"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                            <h3 className="text-white font-bold mb-4">Contact Information</h3>
                            
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="fullName" className="text-white font-medium block mb-2">
                                        Name & Surname <span className="text-red-400">*</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        id="fullName"
                                        name="fullName" 
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 focus:bg-white/20 focus:border-blue-400 focus:outline-none"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="contactNumber" className="text-white font-medium block mb-2">
                                        Contact Number <span className="text-red-400">*</span>
                                    </label>
                                    <input 
                                        type="tel" 
                                        id="contactNumber"
                                        name="contactNumber" 
                                        value={formData.contactNumber}
                                        onChange={handleInputChange}
                                        className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 focus:bg-white/20 focus:border-blue-400 focus:outline-none"
                                        required
                                    />
                                </div>

                                <div>
                                    <label htmlFor="alternateContactNumber" className="text-white font-medium block mb-2">
                                        Alternate Contact Number
                                    </label>
                                    <input 
                                        type="tel" 
                                        id="alternateContactNumber"
                                        name="alternateContactNumber" 
                                        value={formData.alternateContactNumber}
                                        onChange={handleInputChange}
                                        className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 focus:bg-white/20 focus:border-blue-400 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="email" className="text-white font-medium block mb-2">
                                        Email Address <span className="text-red-400">*</span>
                                    </label>
                                    <input 
                                        type="email" 
                                        id="email"
                                        name="email" 
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 focus:bg-white/20 focus:border-blue-400 focus:outline-none"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Terms and Conditions */}
                        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                            <div className="mb-4">
                                <label className="text-white/80 flex items-start gap-3">
                                    <input 
                                        type="checkbox" 
                                        name="termsAccepted" 
                                        checked={formData.termsAccepted}
                                        onChange={handleInputChange}
                                        className="mt-1 text-blue-600"
                                        required
                                    />
                                    <span className="text-sm">
                                        I consent to Miners Market collecting and sharing this information with referral finance providers solely for referral and follow-up purposes. I understand this submission is not a finance application with Miners Market and that any assessment or lending decision will be made independently by the relevant finance provider.
                                    </span>
                                </label>
                            </div>

                            <button 
                                type="submit" 
                                disabled={isSubmitting}
                                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane"></i>
                                        Submit Referral Request
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Disclosure */}
                    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mt-8">
                        <h3 className="text-white font-bold mb-3">Important disclosure</h3>
                        <div className="text-gray-300 text-sm space-y-2">
                            <p>Miners Market acts solely as a referral platform. Miners Market does not provide financing, does not make lending decisions, does not provide financial advice, and is not a registered Financial Services Provider or registered credit provider with the National Credit Regulator.</p>
                            <p>Financing enquiries are referred to our referral finance providers, who will assess the enquiry independently and contact the business directly where applicable.</p>
                            <p>Miners Market does not guarantee funding approval, loan terms, pricing or suitability of any finance product.</p>
                            <p>Partner terms and conditions can be viewed here: <a href="/financeform/terms.html" target="_blank" className="text-blue-400 hover:underline">Referral Finance Provider Terms and Conditions</a></p>
                            <p><strong>Privacy Notice:</strong> By submitting this form, you consent to your information being shared with referral finance providers solely for referral and follow-up purposes.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Hamburger Menu - Always visible */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-50">
                    <div className="absolute inset-0 bg-black/30" onClick={() => setIsMobileMenuOpen(false)}></div>
                    <div className="absolute right-0 w-80 bg-white h-full shadow-xl">
                        <div className="p-4 border-b">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold">Menu</h2>
                                <button 
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>
                        </div>
                        <nav className="p-4">
                            <Link 
                                to="/" 
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md mb-2"
                            >
                                <i className="fas fa-home w-5 mr-3"></i> Home
                            </Link>
                            <Link 
                                to="/equipment" 
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md mb-2"
                            >
                                <i className="fas fa-truck-monster w-5 mr-3"></i> Equipment
                            </Link>
                            <Link 
                                to="/consumables" 
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md mb-2"
                            >
                                <i className="fas fa-box-open w-5 mr-3"></i> Consumables
                            </Link>
                            <Link 
                                to="/miscellaneous" 
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md mb-2"
                            >
                                <i className="fas fa-circle-notch w-5 mr-3"></i> Miscellaneous
                            </Link>
                            <Link 
                                to="/components-and-spares" 
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md mb-2"
                            >
                                <i className="fas fa-cogs w-5 mr-3"></i> Components
                            </Link>
                            <Link 
                                to="/rebuilds" 
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md mb-2"
                            >
                                <i className="fas fa-tools w-5 mr-3"></i> Rebuilds
                            </Link>
                            <Link 
                                to="/rentals" 
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md mb-2"
                            >
                                <i className="fas fa-clock w-5 mr-3"></i> Rentals
                            </Link>
                            <Link 
                                to="/commercial" 
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md mb-2"
                            >
                                <i className="fas fa-briefcase w-5 mr-3"></i> Commercial
                            </Link>
                            <Link 
                                to="/financeform" 
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 px-3 text-green-600 hover:bg-gray-100 rounded-md mb-2 font-semibold"
                            >
                                <i className="fas fa-hand-holding-usd w-5 mr-3"></i> Financing
                            </Link>
                            {user ? (
                                <>
                                    <Link 
                                        to="/dashboard" 
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md mb-2"
                                    >
                                        <i className="fas fa-tachometer-alt w-5 mr-3"></i> Dashboard
                                    </Link>
                                    <button 
                                        onClick={() => {
                                            // Handle logout
                                            setIsMobileMenuOpen(false);
                                        }}
                                        className="block w-full text-left py-2 px-3 text-red-600 hover:bg-gray-100 rounded-md"
                                    >
                                        <i className="fas fa-sign-out-alt w-5 mr-3"></i> Logout
                                    </button>
                                </>
                            ) : (
                                <button 
                                    onClick={() => {
                                        if (onShowLogin) onShowLogin();
                                        setIsMobileMenuOpen(false);
                                    }}
                                    className="block w-full text-left py-2 px-3 text-blue-600 hover:bg-gray-100 rounded-md"
                                >
                                    <i className="fas fa-sign-in-alt w-5 mr-3"></i> Login
                                </button>
                            )}
                        </nav>
                    </div>
                </div>
            )}
        </div>
    );
}
